import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, tokenStore } from '@/api/client';

export type PermEntry = { module: string; tab?: string; view?: boolean; add?: boolean; edit?: boolean; delete?: boolean; export?: boolean };
export type PermAction = 'view' | 'add' | 'edit' | 'delete' | 'export';

interface AuthUser {
  id: number;
  kind: 'user' | 'merchant';
  email: string;
  userType?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  branchId?: number;
  roleId?: number | null;
  permissions?: PermEntry[];
  walletBalance?: number;
  status?: string;
  serviceType?: 'B2C' | 'B2B' | 'BOTH';
  /** Merchant only. True when the account was created by a Shopify App Store install — it is shown
   *  no payment surface anywhere in the product (App Store requirement 1.2.1), including the
   *  post-login redirect. */
  shopifyAppInstall?: boolean;
  // Merchant only. Carried from the login response so the portal can send a self-registered account
  // straight into the Seller Onboarding wizard without an extra round-trip on every page load.
  emailVerified?: boolean;
  onboardingStatus?: 'NOT_STARTED' | 'IN_PROGRESS' | 'SUBMITTED' | 'COMPLETED';
}

/** True while a merchant still has to finish (or has been sent back to) Seller Onboarding. */
export function needsOnboarding(user: AuthUser | null): boolean {
  if (!user || user.kind !== 'merchant') return false;
  return user.onboardingStatus === 'NOT_STARTED' || user.onboardingStatus === 'IN_PROGRESS';
}

/** Can this user perform `action` on `module` → `tab`? SUPER_ADMIN / ADMIN always can. */
export function can(user: AuthUser | null, module: string, tab = 'All', action: PermAction = 'view'): boolean {
  if (!user) return false;
  if (user.userType === 'SUPER_ADMIN' || user.userType === 'ADMIN') return true;
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  const exact = perms.find((p: any) => p.module === module && p.tab === tab);
  if (exact) return !!(exact as any)[action];
  const wide = perms.find((p: any) => p.module === module && (p.tab === undefined || p.tab === null || p.tab === 'All'));
  return !!(wide && (wide as any)[action]);
}

/** Is any tab of `module` viewable (used to show/hide a whole nav entry)? */
export function canModule(user: AuthUser | null, module: string, action: 'view' | 'add' | 'edit' | 'delete' | 'export' = 'view'): boolean {
  if (!user) return false;
  if (user.userType === 'SUPER_ADMIN' || user.userType === 'ADMIN') return true;
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  return perms.some((p: any) => p.module === module && p[action]);
}

/**
 * Does the user's role grant (module, TAB, action)?
 *
 * The module-level `canModule` above answers for a whole module; a module with real sub-tabs (HR,
 * Warehouses, Leads) is granted tab by tab, and the backend `PermissionsGuard` checks exactly this
 * pair. Mirrors `permAllows` on the server: an entry for the tab wins, otherwise a module-wide
 * entry (`tab` absent or 'All') applies to every tab.
 */
export function canModuleTab(
  user: AuthUser | null,
  module: string,
  tab: string,
  action: 'view' | 'add' | 'edit' | 'delete' | 'export' = 'view',
): boolean {
  if (!user) return false;
  if (user.userType === 'SUPER_ADMIN' || user.userType === 'ADMIN') return true;
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  const exact = perms.find((p: any) => p && p.module === module && p.tab === tab);
  if (exact) return !!exact[action];
  const wide = perms.find((p: any) => p && p.module === module && (p.tab === undefined || p.tab === null || p.tab === 'All'));
  return !!(wide && wide[action]);
}

/**
 * Is this user's access controlled by a configured role?
 * Mirrors the backend PermissionsGuard grandfather rule: a user whose role has an
 * empty/absent permission matrix keeps legacy (full) access, so existing internal
 * staff aren't suddenly locked out. Only users with a non-empty permission matrix
 * are restricted to exactly what they've been granted. Admins/merchants are never restricted.
 */
export function isRestricted(user: AuthUser | null): boolean {
  if (!user) return false;
  const t = user.userType;
  // Unrestricted: no known type, admins, and any merchant-portal identity.
  if (!t || t === 'SUPER_ADMIN' || t === 'ADMIN' || t === 'MERCHANT' || t === 'MERCHANT_USER' || t === 'MERCHANT_SUB_USER') return false;
  if (user.kind === 'merchant') return false;
  return Array.isArray(user.permissions) && user.permissions.length > 0;
}

/**
 * Should a nav entry / route bound to permission `module` be shown to this user?
 * Grandfathered (unrestricted) users see everything; restricted users see only granted modules.
 */
export function navAllowed(user: AuthUser | null, module?: string): boolean {
  if (!module) return true;              // entry not part of the permission system
  if (!isRestricted(user)) return true;  // admins + grandfathered users
  return canModule(user, module);
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  impersonation: { name: string } | null;
  setUser: (u: AuthUser | null) => void;
  loginAdmin: (email: string, password: string, captcha?: Captcha) => Promise<void>;
  loginMerchant: (email: string, password: string, captcha?: Captcha) => Promise<void>;
  /** Switch the merchant session to another account linked to this one (no password). */
  switchMerchant: (merchantId: number) => Promise<void>;
  impersonate: (userId: number, name: string) => Promise<void>;
  stopImpersonation: () => void;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

/**
 * The login security code, as the API expects it.
 *
 * Optional all the way down: the mobile app and API clients never send one, and the server
 * only requires it from the web portals (it keys off the `X-App` header this bundle sends).
 */
export type Captcha = { captchaToken: string; captchaAnswer: string };

const IMP_TOKEN_KEY = 'imp-return-token';
const IMP_USER_KEY = 'imp-return-user';

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      impersonation: null,
      setUser: (u) => set({ user: u, isAuthenticated: !!u }),
      loginAdmin: async (email, password, captcha) => {
        const r = await api.post('/auth/login/admin', { email, password, ...captcha });
        tokenStore.set(r.data.accessToken);
        const u = r.data.user;
        set({
          user: {
            id: u.id, kind: 'user', email: u.email, userType: u.userType,
            firstName: u.firstName, lastName: u.lastName, branchId: u.branchId,
            roleId: u.roleId ?? null,
            permissions: u.permissions,
          },
          isAuthenticated: true,
        });
      },
      loginMerchant: async (email, password, captcha) => {
        const r = await api.post('/auth/login/merchant', { email, password, ...captcha });
        tokenStore.set(r.data.accessToken);
        const m = r.data.merchant;
        set({
          user: {
            id: m.id, kind: 'merchant', email: m.email,
            companyName: m.companyName, walletBalance: m.walletBalance, status: m.status,
            serviceType: m.serviceType,
            emailVerified: m.emailVerified, onboardingStatus: m.onboardingStatus,
            // Known at login, so the redirect can avoid the wallet for an App Store account without
            // waiting on a profile fetch that happens after the page has already changed.
            shopifyAppInstall: m.shopifyAppInstall === true,
          },
          isAuthenticated: true,
        });
      },
      // Switch to a sister merchant account. The server issues a NEW access token AND rotates the
      // refresh cookie, so the switch survives a silent token refresh — swapping only the access
      // token would leave the cookie pointing at the previous account and drop the user back into it
      // the next time the token was refreshed.
      switchMerchant: async (merchantId) => {
        const r = await api.post(`/auth/switch-merchant/${merchantId}`);
        tokenStore.set(r.data.accessToken);
        const m = r.data.merchant;
        set({
          user: {
            id: m.id, kind: 'merchant', email: m.email,
            companyName: m.companyName, walletBalance: m.walletBalance, status: m.status,
            serviceType: m.serviceType,
          },
          isAuthenticated: true,
        });
      },
      // Super-admin "Log in as" — swap the active token for an impersonation token, stashing the
      // admin's own session so it can be restored via stopImpersonation().
      impersonate: async (userId, name) => {
        const adminToken = tokenStore.get();
        const adminUser = get().user;
        if (adminToken) localStorage.setItem(IMP_TOKEN_KEY, adminToken);
        if (adminUser) localStorage.setItem(IMP_USER_KEY, JSON.stringify(adminUser));
        const r = await api.post(`/auth/impersonate/${userId}`);
        tokenStore.set(r.data.accessToken);
        const u = r.data.user;
        set({
          user: {
            id: u.id, kind: 'user', email: u.email, userType: u.userType,
            firstName: u.firstName, lastName: u.lastName, branchId: u.branchId,
            roleId: u.roleId ?? null, permissions: u.permissions,
          },
          isAuthenticated: true,
          impersonation: { name },
        });
      },
      stopImpersonation: () => {
        const t = localStorage.getItem(IMP_TOKEN_KEY);
        const uStr = localStorage.getItem(IMP_USER_KEY);
        localStorage.removeItem(IMP_TOKEN_KEY);
        localStorage.removeItem(IMP_USER_KEY);
        if (t) tokenStore.set(t);
        set({ user: uStr ? JSON.parse(uStr) : get().user, isAuthenticated: true, impersonation: null });
        window.location.href = '/dashboard';
      },
      logout: async () => {
        localStorage.removeItem(IMP_TOKEN_KEY);
        localStorage.removeItem(IMP_USER_KEY);
        try { await api.post('/auth/logout'); } catch { /* ignore */ }
        tokenStore.set(null);
        set({ user: null, isAuthenticated: false, impersonation: null });
        window.location.href = '/login';
      },
      /**
       * Re-read the signed-in identity -- above all, the CURRENT permission matrix.
       *
       * The store is persisted to localStorage, so without this a user carries whatever permissions
       * they had at the moment they logged in, for as long as their session lasts. Grant someone a
       * module today and they still cannot see it tomorrow; the only cure was a full logout, which
       * is why "I gave them the permission but it is not showing" kept coming back. Called once per
       * page load (see `useSessionSync`), so a refresh is now enough.
       *
       * Merged rather than replaced: /auth/me is authoritative for identity and permissions but does
       * not carry everything login put in the store, and a blind overwrite would silently drop those
       * fields. Only a 401 clears the session -- a network blip must not sign anyone out.
       */
      refreshMe: async () => {
        try {
          const r = await api.get('/auth/me');
          const fresh = r.data ?? {};
          const prev = get().user;
          // A token that now belongs to someone else (impersonation, account switch) replaces the
          // stored user outright; merging two different people would mix their permissions.
          const sameIdentity = prev && prev.id === fresh.id && prev.kind === fresh.kind;
          set({ user: (sameIdentity ? { ...prev, ...fresh } : fresh) as AuthUser, isAuthenticated: true });
        } catch (e: any) {
          if (e?.response?.status === 401) set({ user: null, isAuthenticated: false });
        }
      },
    }),
    {
      name: 'courier-auth',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated, impersonation: state.impersonation }),
    },
  ),
);

/**
 * Pull fresh permissions once per page load.
 *
 * Deliberately fire-and-forget and outside React state: the screen renders immediately from the
 * persisted user, and the nav re-renders by itself the moment the store updates. A module-level flag
 * (not a ref) keeps it to one call per page load no matter how many routes mount the guard.
 */
let sessionSynced = false;
export function useSessionSync() {
  useEffect(() => {
    if (sessionSynced) return;
    if (!tokenStore.get()) return;
    sessionSynced = true;
    void useAuth.getState().refreshMe();
  }, []);
}
